import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, TrendingUp, Landmark, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => (n ? Number(n).toLocaleString("en-IN") : "—");

/**
 * Monthly Price Pulse — monumental executive bulletin.
 *
 * Renders the most recently published price-ledger snapshot with a
 * Cinzel headline that scales to the month currently in publication.
 * The month name updates automatically when a new PDF/DOCX is
 * uploaded via the Admin Auto-Update flow.
 */
export function MonthlyPricePulse() {
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/price-ledger/latest`)
      .then((r) => setLedger(r.data?.ledger || null))
      .catch(() => setLedger(null))
      .finally(() => setLoading(false));
  }, []);

  const topSteel = useMemo(
    () => (ledger?.entries || []).filter((e) => e.category === "steel").slice(0, 5),
    [ledger]
  );
  const topCement = useMemo(
    () => (ledger?.entries || []).filter((e) => e.category === "cement").slice(0, 6),
    [ledger]
  );

  if (loading || !ledger) return null;
  const s = ledger.summary || {};

  return (
    <section
      id="monthly-price-pulse"
      data-testid="monthly-price-pulse"
      className="relative py-24 md:py-32 border-t border-white/10 overflow-hidden"
    >
      {/* Ambient copper glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            "radial-gradient(ellipse at 20% -10%, rgba(184,115,51,0.16), transparent 55%), radial-gradient(ellipse at 80% 110%, rgba(216,178,62,0.10), transparent 55%)",
        }}
      />

      <div className="relative max-w-[1300px] mx-auto px-6 md:px-12">
        {/* Kicker */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-center justify-between gap-6 border-b border-white/10 pb-8"
        >
          <div>
            <div className="flex items-center gap-3">
              <span className="h-px w-14 bg-[#B87333]" />
              <span
                className="uppercase text-[11px] tracking-[0.42em] text-[#B87333]"
                style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}
              >
                Official Bulletin
              </span>
            </div>
            <div className="mt-4 flex items-baseline gap-4 flex-wrap">
              <h2
                data-testid="pulse-month-headline"
                className="text-[clamp(3rem,8vw,7.5rem)] leading-[0.9] tracking-[0.04em]"
                style={{ fontFamily: "'Cinzel', 'Playfair Display', serif", fontWeight: 700 }}
              >
                <span className="bg-gradient-to-b from-[#F5F5F0] to-[#E6C88A] bg-clip-text text-transparent drop-shadow-[0_0_24px_rgba(216,178,62,0.25)]">
                  {ledger.month_label}
                </span>
              </h2>
              <span
                className="uppercase text-[11px] tracking-[0.32em] text-[#8A8A82]"
                style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}
              >
                Price Pulse
              </span>
            </div>
            <p className="font-body text-sm md:text-base text-[#C9C9C2] mt-6 max-w-2xl leading-relaxed">
              A single monumental snapshot of the trade desk's verified rates for the month —
              scanned in from the source ledger and republished the moment the next monthly
              file is uploaded.
            </p>
          </div>

          <div className="flex flex-col items-end gap-3 shrink-0">
            <span
              className="inline-flex items-center gap-2 rounded-full border border-[#2FB999]/40 bg-[#2FB999]/[0.08] px-4 py-2"
              data-testid="pulse-live-badge"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2FB999] opacity-70" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#2FB999]" />
              </span>
              <span
                className="uppercase text-[10px] tracking-[0.28em] text-[#2FB999]"
                style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}
              >
                Ledger Verified · Live
              </span>
            </span>
            <div className="text-right">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A8A82]">
                {(s.brands || []).length} brands · {s.total || 0} SKUs
              </div>
              <div className="font-body text-[11px] text-[#8A8A82] mt-1">
                Published {new Date(ledger.published_at || ledger.uploaded_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Two-column headline: Steel + Cement */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="pulse-summary-tiles">
          <SummaryTile
            icon={<TrendingUp size={18} />}
            label="Steel · TMT"
            unit="per MT"
            count={s.steel?.count || 0}
            range={s.steel?.range_per_mt}
            accent="#B87333"
            testid="pulse-tile-steel"
          />
          <SummaryTile
            icon={<Landmark size={18} />}
            label="Cement"
            unit="per 50 kg bag"
            count={s.cement?.count || 0}
            range={s.cement?.range_per_bag}
            accent="#D8B23E"
            testid="pulse-tile-cement"
          />
        </div>

        {/* Brand rows */}
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10" data-testid="pulse-brand-columns">
          <BrandColumn title="Verified Steel Ledger" rows={topSteel} unit="per MT" accent="#B87333" testid="pulse-col-steel" />
          <BrandColumn title="Verified Cement Ledger" rows={topCement} unit="per bag" accent="#D8B23E" testid="pulse-col-cement" />
        </div>

        {/* Footer stripe */}
        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6">
          <div className="flex items-center gap-3 text-[#8A8A82]">
            <ShieldCheck size={14} className="text-[#B87333]" />
            <span
              className="uppercase text-[10px] tracking-[0.24em]"
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}
              data-testid="pulse-source"
            >
              Source · Official Vinci Trade Network Ledger
            </span>
          </div>
          <a
            href="/trends"
            data-testid="pulse-open-terminal"
            className="inline-flex items-center gap-2 rounded-full border border-[#B87333]/60 hover:border-[#B87333] text-[#B87333] px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em]"
          >
            Open trading terminal <ArrowRight size={12} />
          </a>
        </div>
      </div>
    </section>
  );
}

function SummaryTile({ icon, label, unit, count, range, accent, testid }) {
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="relative overflow-hidden rounded-sm border border-white/10 bg-[#0a0a0a]/70 p-7"
      style={{ boxShadow: `inset 0 0 0 1px ${accent}18` }}
    >
      <div className="absolute inset-x-0 top-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)`, opacity: 0.55 }} />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-full" style={{ background: `${accent}25`, color: accent }}>{icon}</span>
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.24em]" style={{ color: accent }}>{label}</div>
            <div className="font-body text-[11px] text-[#8A8A82] mt-1">{count} brands tracked · {unit}</div>
          </div>
        </div>
        <Sparkles size={16} style={{ color: accent }} className="opacity-70" />
      </div>
      {range ? (
        <div className="mt-6">
          <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82]">Ledger Range</div>
          <div
            className="mt-1 leading-none"
            style={{ fontFamily: "'Cinzel', 'Playfair Display', serif", fontWeight: 700, fontSize: "clamp(2.1rem, 4vw, 3.2rem)" }}
          >
            <span className="text-[#F5F5F0]">₹{fmt(range.min)}</span>
            <span className="mx-3 text-[#8A8A82]" style={{ fontSize: "0.55em" }}>—</span>
            <span style={{ color: accent }}>₹{fmt(range.max)}</span>
          </div>
        </div>
      ) : (
        <div className="mt-6 text-[#8A8A82] font-body text-sm">Awaiting the next ledger upload.</div>
      )}
    </motion.div>
  );
}

function BrandColumn({ title, rows, unit, accent, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-3 mb-4">
        <span className="h-px w-8" style={{ background: accent }} />
        <span
          className="uppercase text-[10px] tracking-[0.28em]"
          style={{ color: accent, fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}
        >
          {title}
        </span>
      </div>
      <div className="space-y-1">
        {rows.length === 0 ? (
          <div className="text-[#8A8A82] font-body text-sm py-6">No entries.</div>
        ) : rows.map((r, i) => (
          <div
            key={`${r.brand}-${r.product}-${i}`}
            data-testid={`pulse-row-${testid}-${i}`}
            className="grid grid-cols-[1fr_auto] gap-4 items-baseline border-b border-white/5 py-3"
          >
            <div className="min-w-0">
              <div className="font-serif text-lg text-[#F5F5F0] truncate">{r.brand}</div>
              {(r.product || r.region) && (
                <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mt-1 truncate">
                  {[r.product, r.region].filter(Boolean).join("  ·  ")}
                </div>
              )}
            </div>
            <div className="text-right tabular-nums">
              <div className="text-[#F5F5F0]" style={{ fontFamily: "'Cinzel', serif", fontWeight: 600, fontSize: "1.15rem" }}>
                ₹{fmt(r.price_min)}
                {r.price_max > r.price_min && (
                  <span className="text-[#8A8A82] text-sm"> – ₹{fmt(r.price_max)}</span>
                )}
              </div>
              <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mt-1">{unit}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
